/* eslint-disable react/no-unknown-property */

import { Close } from '@audio-network/componentlibrary/icons'
import React, { useCallback, useEffect, useRef, useState } from 'react'

import { useAnimationState } from '@/hooks/useAnimationState'
import { usePopover } from '@/hooks/usePopover'
import { useWaitForElementToBeInDOM } from '@/hooks/useWaitForElementToBeInDOM'

import styles from './Spotlight.module.css'
import { SpotlightProps } from './Spotlight.types'
import {
	hasSeenSpotlight,
	isSpotlightSupported,
	positionSpotlight,
	setupPositionTracking,
	SPOTLIGHT_CONSTANTS,
	toggleSpotlight,
} from './Spotlight.utils'

const SpotlightWrapper = (props: SpotlightProps): JSX.Element => {
	const [isSupported, setIsSupported] = useState(false)
	const [showSpotlight, setShowSpotlight] = useState(false)

	useEffect(() => {
		if (!isSpotlightSupported()) return

		const hasSeen = hasSeenSpotlight(props.name)
		setShowSpotlight(!hasSeen)
		setIsSupported(true)

		const mql = window?.matchMedia?.(SPOTLIGHT_CONSTANTS.MIN_WIDTH_MEDIA_QUERY)
		const handleMediaChange = (event: MediaQueryListEvent): void => {
			setIsSupported(event.matches)
		}

		mql.addEventListener('change', handleMediaChange)
		return () => mql.removeEventListener('change', handleMediaChange)
	}, [props.name])

	if (!isSupported || !showSpotlight) return null
	return <Spotlight {...props} />
}

const Spotlight = ({
	name,
	describes,
	children,
	...props
}: SpotlightProps): JSX.Element => {
	const [isOpen, setIsOpen] = useState(false)
	const spotlightRef = useRef<HTMLDivElement>(null)

	const handlePositionUpdate = useCallback(
		(isAnimating: boolean) => {
			positionSpotlight(describes, spotlightRef, isAnimating)
		},
		[describes, spotlightRef]
	)

	const { isAnimatingRef } = useAnimationState({
		ref: spotlightRef,
		onAnimationEnd: () => handlePositionUpdate(false),
		onAnimationStart: () => handlePositionUpdate(true),
	})

	const handleShow = useCallback(() => {
		toggleSpotlight(true, spotlightRef, describes, name, styles.pulse)
		setIsOpen(true)
	}, [describes, name, spotlightRef])

	const handleHide = useCallback(() => {
		toggleSpotlight(false, spotlightRef, describes, name, styles.pulse)
		setIsOpen(false)
	}, [describes, name, spotlightRef])

	useWaitForElementToBeInDOM({
		selector: describes,
		onElementFound: handleShow,
	})

	usePopover({
		ref: spotlightRef,
		onClose: handleHide,
	})

	useEffect(() => {
		if (!isOpen || !describes) return

		const describedElement = document.querySelector(describes)
		const panel = spotlightRef.current
		if (!describedElement || !panel) return

		const cleanup = setupPositionTracking(() =>
			handlePositionUpdate(isAnimatingRef.current)
		)

		return () => {
			cleanup()
		}
	}, [isOpen, describes, handlePositionUpdate, spotlightRef, isAnimatingRef])

	// Cleanup pulse class on unmount
	useEffect(() => {
		return () => {
			const target = document.querySelector(describes)
			if (target instanceof HTMLElement) {
				target.classList.remove(styles.pulse)
			}
		}
	}, [describes])

	return (
		<div
			{...props}
			ref={spotlightRef}
			popover="auto"
			data-testid="spotlight-panel"
			className={styles.spotlightPanel}
			aria-atomic
			aria-live="assertive"
		>
			<div>{children}</div>
			<Close
				title="Close"
				onClick={handleHide}
				className={styles.closeButton}
			/>
		</div>
	)
}

export default SpotlightWrapper
